import { Alert, Box, Button, CircularProgress, Link, Stack, TextField } from "@mui/material";
import { Text } from "@src/lib/ui-react";
import { type ReactElement, useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { LoginLayout } from "../../../containers/enter/commonLogin/loginLayout";
import { APP_V2_APP_EVENTS, logError, logEvent } from "../../lib/analytics";
import { showErrorToast } from "../../lib/Notifications";
import { getExistingSessionEmail } from "../cognito/cognitoAuth";
import { confirmPasswordReset } from "../cognito/cognitoPasswordAuth";
import { mapCognitoError } from "../cognito/mapCognitoError";

const PASSWORD_MIN_LENGTH = 12;

type ResetStep = "confirm" | "success";

export function ResetPassword(): ReactElement {
  const history = useHistory();
  const location = useLocation();
  const searchParameters = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const codeFromUrl = searchParameters.get("code");
  const emailFromUrl = searchParameters.get("email");

  const [step, setStep] = useState<ResetStep>("confirm");
  // Store URL params in state so they survive the replaceState call that strips the URL
  // eslint-disable-next-line react/hook-use-state
  const [email] = useState(() => emailFromUrl ?? "");
  // eslint-disable-next-line react/hook-use-state
  const [code] = useState(() => codeFromUrl ?? "");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [hasActiveSession, setHasActiveSession] = useState(false);

  useEffect(() => {
    void getExistingSessionEmail().then((sessionEmail) => {
      setHasActiveSession(sessionEmail !== undefined);
    });
  }, []);

  useEffect(() => {
    if (codeFromUrl) {
      globalThis.history.replaceState(null, "", globalThis.location.pathname);
    }
  }, [codeFromUrl]);

  useEffect(() => {
    // Guard against direct navigation without code/email params.
    // Check the state values (not URL-derived values) because the first useEffect
    // strips the URL params via replaceState — on re-renders the URL-derived values
    // would be null even though the state values are still valid.
    if (!email || !code) {
      history.replace("/v2/login-with-email-password");
    }
  }, [email, code, history]);

  const handleConfirmReset = async (formEvent: React.FormEvent) => {
    formEvent.preventDefault();
    setError(undefined);

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    if (newPassword.length < PASSWORD_MIN_LENGTH) {
      setError(`Password must be at least ${PASSWORD_MIN_LENGTH} characters.`);
      return;
    }

    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword)) {
      setError("Password must include at least one uppercase letter and one lowercase letter.");
      return;
    }

    setIsSubmitting(true);

    try {
      await confirmPasswordReset(email, code, newPassword);
      logEvent(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_CONFIRMED);
      setStep("success");
    } catch (authError: unknown) {
      const mappedError = mapCognitoError(authError, "password");
      logError(APP_V2_APP_EVENTS.COGNITO_PASSWORD_RESET_CONFIRM_FAILURE, {
        error: authError,
        metadata: { errorCode: mappedError.code, errorMessage: mappedError.message, email },
      });

      setError(mappedError.message);
      showErrorToast(mappedError.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (step === "success") {
    return (
      <LoginLayout>
        <Stack alignItems="center" spacing={2.5}>
          <Alert severity="success" sx={{ width: "100%" }}>
            Your password has been reset successfully.
          </Alert>
          {!hasActiveSession && (
            <Button
              fullWidth
              variant="contained"
              onClick={() => {
                history.push("/v2/login-with-email-password");
              }}
            >
              Sign in with your new password
            </Button>
          )}
        </Stack>
      </LoginLayout>
    );
  }

  return (
    <LoginLayout>
      <Stack spacing={2.5}>
        <Text align="center" variant="h4">
          Set new password
        </Text>

        {error && (
          <Alert severity="error" sx={{ textAlign: "left" }}>
            {error}
          </Alert>
        )}

        <Box
          noValidate
          component="form"
          onSubmit={(formEvent: React.FormEvent) => {
            void handleConfirmReset(formEvent);
          }}
        >
          <Stack spacing={2}>
            <Text align="left" color="text.secondary" variant="body2">
              Email: {email}
            </Text>
            <TextField
              fullWidth
              required
              autoComplete="new-password"
              disabled={isSubmitting}
              label="New Password"
              size="small"
              type="password"
              value={newPassword}
              onChange={(event) => {
                setNewPassword(event.target.value);
              }}
            />
            <TextField
              fullWidth
              required
              autoComplete="new-password"
              disabled={isSubmitting}
              label="Confirm New Password"
              size="small"
              type="password"
              value={confirmPassword}
              onChange={(event) => {
                setConfirmPassword(event.target.value);
              }}
            />
            <Text color="text.secondary" variant="caption">
              Password must be at least {PASSWORD_MIN_LENGTH} characters and include uppercase and
              lowercase letters.
            </Text>
            <Button
              fullWidth
              disabled={isSubmitting || !newPassword || !confirmPassword || !email}
              type="submit"
              variant="contained"
            >
              {isSubmitting ? <CircularProgress size={24} /> : "Reset Password"}
            </Button>
            {!hasActiveSession && (
              <Link
                component="button"
                sx={{ alignSelf: "center" }}
                type="button"
                underline="hover"
                variant="body2"
                onClick={() => {
                  history.push("/v2/login-with-email-password");
                }}
              >
                Back to sign in with password
              </Link>
            )}
          </Stack>
        </Box>
      </Stack>
    </LoginLayout>
  );
}
